import React, { useEffect, useRef } from 'react';
import { Car, Briefcase, ArrowRight, Clock, Users, Luggage } from 'lucide-react';
import { trackEvent as trackTPEvent } from '../../utils/tracking';
import { trackEvent, trackAffiliateRedirect, trackTripCompletion } from '../../utils/analytics';
import { AFFILIATE_MARKER } from '../../utils/affiliate';
import {
  getAffiliateServiceUrl,
  isAffiliateServiceEnabled,
  getAffiliateServiceLabel,
} from '../../config/affiliateServices';

/**
 * GroundTransportSection — iter66 auto-prefill upgrade.
 *
 * Renders the airport-transfer + rental-car affiliate options the
 * backend built in `services/ground_transport.py`. Slots in INLINE
 * below the FlightList so the booking flow reads naturally:
 *
 *   Trip plan → Hotel → Flight → Transfer → Rental car
 *
 * iter103 — Kiwitaxi + Localrent affiliate URL integration.
 * When `REACT_APP_KIWITAXI_AFFILIATE_URL` /
 * `REACT_APP_LOCALRENT_AFFILIATE_URL` are configured in
 * `/app/frontend/.env`, those URLs OVERRIDE the backend-built deep
 * link. This routes outbound clicks through the Travelpayouts short
 * redirect so commissions are attributed correctly. The backend
 * continues to compute dates/party/pickup-time for the visible chips.
 *
 * Props:
 *   groundTransport — { carRental?, transfer?, renderHint } or null.
 *                     Each sub-object carries url + summary + IATA +
 *                     dates + travelers + adults + children + infants +
 *                     pickupTime + (carRental: dropoffTime + driverAge) +
 *                     (transfer: luggageLarge + luggageSmall + driveMinutes).
 *   surface — optional analytics label ("chat" | "trip_summary" | ...).
 */
export default function GroundTransportSection({ groundTransport, surface = 'chat' }) {
  const rootRef = useRef(null);
  const viewedRef = useRef({ transfer: false, carRental: false });

  // Resolve the actual outbound URL for each kind. Prefer the
  // configured Travelpayouts affiliate shortlink; fall back to the
  // backend-built deep link only when the env var is missing.
  const transferUrl = isAffiliateServiceEnabled('kiwitaxi')
    ? getAffiliateServiceUrl('kiwitaxi')
    : (groundTransport?.transfer?.url || '');
  const carRentalUrl = isAffiliateServiceEnabled('localrent')
    ? getAffiliateServiceUrl('localrent')
    : (groundTransport?.carRental?.url || '');

  // Fire ONE `affiliate_service_view` event per service when the
  // component scrolls into viewport. Uses IntersectionObserver — never
  // throws, never counts the same session twice.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (groundTransport?.transfer && !viewedRef.current.transfer && transferUrl) {
          viewedRef.current.transfer = true;
          try {
            trackEvent('affiliate_service_view', {
              service: 'kiwitaxi',
              surface,
              destination_iata: groundTransport.transfer.pickupIata || null,
              destination: groundTransport.transfer.destinationShort
                || groundTransport.transfer.destination
                || null,
            });
          } catch { /* analytics best-effort */ }
        }
        if (groundTransport?.carRental && !viewedRef.current.carRental && carRentalUrl) {
          viewedRef.current.carRental = true;
          try {
            trackEvent('affiliate_service_view', {
              service: 'localrent',
              surface,
              destination_iata: groundTransport.carRental.pickupIata || null,
              destination: groundTransport.carRental.destinationShort
                || groundTransport.carRental.destination
                || null,
            });
          } catch { /* analytics best-effort */ }
        }
      });
    }, { threshold: 0.5 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [groundTransport, transferUrl, carRentalUrl, surface]);

  if (!groundTransport) return null;
  const { transfer, carRental } = groundTransport;
  if (!transfer && !carRental) return null;

  const handleClick = (kind, partner, url, opt = {}) => {
    if (!url) return;
    const service = kind === 'car_rental' ? 'localrent' : 'kiwitaxi';
    try {
      trackTPEvent('ground_transport_click', { kind, partner });
    } catch {
      /* analytics is best-effort */
    }
    // iter103 — primary CTA event for Kiwitaxi/Localrent surfaces.
    try {
      trackEvent('affiliate_service_cta_click', {
        service,
        surface,
        partner: service === 'kiwitaxi'
          ? getAffiliateServiceLabel('kiwitaxi')
          : getAffiliateServiceLabel('localrent'),
        destination_iata: opt?.pickupIata || null,
        destination: opt?.destinationShort || opt?.destination || null,
        cta_id: service === 'kiwitaxi' ? 'find-transfer' : 'find-rental-car',
      });
    } catch { /* analytics best-effort */ }
    // Phase-2 mandatory affiliate telemetry — same event schema used by
    // flight/hotel redirects so funnel dashboards slice by type.
    try {
      trackAffiliateRedirect({
        partner: service,
        type: service === 'kiwitaxi' ? 'taxi' : 'car',
        origin: opt?.pickupIata || null,
        destination: opt?.dropoffIata || opt?.pickupIata || null,
        price: typeof opt?.estimatedFromEur === 'number' ? opt.estimatedFromEur : null,
        marker: AFFILIATE_MARKER || null,
        surface: 'ground_transport',
        item_id: opt?.id || `${service}-${opt?.pickupIata || 'x'}`,
        reason_chips: opt?.reasonChips || null,
      });
      trackTripCompletion({ surface: 'ground_transport', destination: opt?.dropoffIata || opt?.pickupIata || null });
    } catch { /* analytics must never block navigation */ }
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = url;
    }
  };

  // Small helper — render "2 adults · 2 kids · 1 infant" from the
  // per-pax breakdown the backend sent. Falls back to total `travelers`
  // when only an aggregate is present.
  const renderPartySummary = (opt) => {
    if (!opt) return null;
    const parts = [];
    if (opt.adults > 0) parts.push(`${opt.adults} ${opt.adults === 1 ? 'adult' : 'adults'}`);
    if (opt.children > 0) parts.push(`${opt.children} ${opt.children === 1 ? 'kid' : 'kids'}`);
    if (opt.infants > 0) parts.push(`${opt.infants} infant${opt.infants === 1 ? '' : 's'}`);
    if (parts.length === 0 && opt.travelers > 0) {
      parts.push(`${opt.travelers} ${opt.travelers === 1 ? 'traveler' : 'travelers'}`);
    }
    return parts.join(' · ');
  };

  return (
    <div
      ref={rootRef}
      className="mt-3 mb-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
      data-testid="ground-transport-section"
    >
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600/10 text-emerald-700">
          <ArrowRight className="h-3.5 w-3.5" />
        </div>
        <h4 className="text-sm font-semibold text-slate-900" data-testid="ground-transport-title">
          Getting from the airport
        </h4>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {transfer && transferUrl && (
          <button
            type="button"
            onClick={() => handleClick('transfer', getAffiliateServiceLabel('kiwitaxi') || transfer.partner, transferUrl, transfer)}
            className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
            data-testid="affiliate-service-kiwitaxi"
            aria-label="Airport Transfers — Find a Transfer with Kiwitaxi"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <Briefcase className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">
                    Airport Transfers
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {getAffiliateServiceLabel('kiwitaxi') || transfer.partner}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-600">
              {transfer.summary || 'Explore private transfer options between the airport and your accommodation.'}
            </p>
            {/* iter66 — auto-prefill chips: party + pickup time + luggage. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="ground-transport-transfer-prefill">
              {renderPartySummary(transfer) && (
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {renderPartySummary(transfer)}
                </span>
              )}
              {transfer.pickupTime && (
                <span className="inline-flex items-center gap-1" data-testid="transfer-pickup-time">
                  <Clock className="h-3 w-3" />
                  Pickup {transfer.pickupTime}
                </span>
              )}
              {(transfer.luggageLarge > 0 || transfer.luggageSmall > 0) && (
                <span className="inline-flex items-center gap-1" data-testid="transfer-luggage">
                  <Luggage className="h-3 w-3" />
                  {transfer.luggageLarge}L
                  {transfer.luggageSmall > 0 ? ` · ${transfer.luggageSmall}S` : ''}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {transfer.driveMinutes
                  ? `${Math.floor(transfer.driveMinutes / 60) ? `${Math.floor(transfer.driveMinutes / 60)}h ` : ''}${transfer.driveMinutes % 60}min`
                  : 'Door-to-door'}
              </span>
              <span className="font-medium text-emerald-700 group-hover:underline" data-testid="kiwitaxi-cta">
                Find a Transfer →
              </span>
            </div>
          </button>
        )}

        {carRental && carRentalUrl && (
          <button
            type="button"
            onClick={() => handleClick('car_rental', getAffiliateServiceLabel('localrent') || carRental.partner, carRentalUrl, carRental)}
            className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md"
            data-testid="affiliate-service-localrent"
            aria-label="Car Rentals — Find a Rental Car with Localrent"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                  <Car className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-sky-700">
                    Car Rentals
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {getAffiliateServiceLabel('localrent') || carRental.partner}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-600">
              {carRental.summary || 'Explore rental-car options at your destination.'}
            </p>
            {/* iter66 — auto-prefill chips: party + pickup/dropoff clock. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500" data-testid="ground-transport-car-prefill">
              {renderPartySummary(carRental) && (
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {renderPartySummary(carRental)}
                </span>
              )}
              {carRental.pickupTime && (
                <span className="inline-flex items-center gap-1" data-testid="car-pickup-time">
                  <Clock className="h-3 w-3" />
                  Pickup {carRental.pickupTime}
                </span>
              )}
              {carRental.dropoffTime && (
                <span className="inline-flex items-center gap-1" data-testid="car-dropoff-time">
                  <Clock className="h-3 w-3" />
                  Drop-off {carRental.dropoffTime}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                {carRental.rentalDays
                  ? `${carRental.rentalDays}-day rental · ${carRental.pickupIata || ''}`
                  : (carRental.pickupIata || '')}
              </span>
              <span className="font-medium text-sky-700 group-hover:underline" data-testid="localrent-cta">
                Find a Rental Car →
              </span>
            </div>
          </button>
        )}
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
        You'll see live availability and the exact total on the partner's site before booking.
      </p>
    </div>
  );
}
